/* eslint-disable react/prop-types */
import {
  Box,
  Chip,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import React from 'react'

import VoiceCapture from 'app/components/VoiceCapture'
import { eventEmitter } from 'app/helpers'
import {
  INTERACTION_TEMPLATE_VALIDATE,
  isTemplateFieldVisible,
  renderTemplateSummary
} from 'app/pages/Communication/InteractionHistory/templates'

// Renders a structured interaction template's fields and reports BOTH the raw
// answers (structured_data JSON) and an auto-rendered readable summary (HTML)
// back to the parent on every change. Used inside the interaction form's topic
// widget in place of the free-text summary when the topic has a template.
const InteractionTemplate = ({
  template,
  topic = '',
  data = {},
  subjectOptions = [],
  onChange
}) => {
  const emit = (next) => {
    onChange(next, renderTemplateSummary(template, next))
  }
  const setField = (key, value) => emit({ ...data, [key]: value })

  // The field defs we send the backend so it extracts into the right keys. For
  // the subjects field we pass the centre's subject list as the option set so
  // spoken program names snap to the real subjects.
  const voiceFields = template.fields.map((f) => ({
    key: f.key,
    label: f.label,
    type: f.type,
    options: f.type === 'subjects' ? subjectOptions : f.options
  }))

  // Merge the spoken answers over whatever is already filled (VoiceCapture
  // always calls the latest closure, so `data` here is current). Only the
  // fields Gemini actually heard are present, so nothing gets wiped.
  const onVoiceResult = (result) => {
    const filled = (result && result.structured_data) || {}
    emit({ ...data, ...filled })
  }

  // Seed the parent's summary/structured_data as soon as the template is shown
  // (topic switched to a templated one), so saving without editing still yields
  // a non-blank summary. Runs once on mount.
  React.useEffect(() => {
    emit(data || {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Every visible field is mandatory. We don't paint the form red the moment it
  // opens — the blanks only light up once the team actually tries to save and
  // the Interactions page blocks it (which broadcasts this event).
  const [showErrors, setShowErrors] = React.useState(false)
  React.useEffect(() => {
    const handler = () => setShowErrors(true)
    eventEmitter.addListener(INTERACTION_TEMPLATE_VALIDATE, handler)
    return () =>
      eventEmitter.removeListener(INTERACTION_TEMPLATE_VALIDATE, handler)
  }, [])

  const isBlank = (f) => {
    const val = data[f.key]
    if (Array.isArray(val)) return val.length === 0
    return val === undefined || val === null || String(val).trim() === ''
  }

  return (
    <Box mt={1}>
      <Box
        mb={1.5}
        display="flex"
        alignItems="center"
        style={{ gap: 10, flexWrap: 'wrap' }}
      >
        <VoiceCapture
          topic={topic}
          fields={voiceFields}
          onResult={onVoiceResult}
          tooltip="Speak the update — Alfred fills the fields below"
        />
        <Typography variant="caption" color="textSecondary">
          Speak the update and the fields fill in — please review before saving.
        </Typography>
      </Box>
      {template.fields.map((f) => {
        // Conditional field: only render when its showWhen condition is met
        // (e.g. "If no, reason" appears only when the answer is No).
        if (!isTemplateFieldVisible(f, data)) {
          return null
        }
        const missing = showErrors && isBlank(f)

        // The label sits ABOVE the box, not on the outline. MUI v4's notched
        // outline measures the label's width on first render, which inside this
        // modal comes back as 0 — the notch never opens and a shrunk label ends
        // up struck through by the border. A plain label above the field avoids
        // the notch altogether and keeps the 10-field templates readable.
        const fieldLabel = (
          <Typography
            variant="caption"
            display="block"
            color={missing ? 'error' : 'textSecondary'}
            style={{ marginBottom: 2 }}
          >
            {f.label}*
          </Typography>
        )
        const requiredHint = missing ? (
          <FormHelperText error>Required</FormHelperText>
        ) : null

        if (f.type === 'subjects') {
          return (
            <Box key={f.key} mb={1.5}>
              {fieldLabel}
              <Autocomplete
                multiple
                size="small"
                options={subjectOptions}
                value={Array.isArray(data[f.key]) ? data[f.key] : []}
                onChange={(_e, val) => setField(f.key, val)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      size="small"
                      label={option}
                      {...getTagProps({ index })}
                      key={option}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    error={missing}
                    placeholder="Type to search subjects"
                  />
                )}
              />
              {requiredHint}
            </Box>
          )
        }

        if (f.type === 'select') {
          return (
            <Box key={f.key} mb={1.5}>
              {fieldLabel}
              <FormControl variant="outlined" size="small" fullWidth error={missing}>
                <Select
                  displayEmpty
                  value={data[f.key] || ''}
                  onChange={(e) => setField(f.key, e.target.value)}
                >
                  <MenuItem value="">
                    <span style={{ color: '#9e9e9e' }}>Select…</span>
                  </MenuItem>
                  {f.options.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
                {requiredHint}
              </FormControl>
            </Box>
          )
        }

        // text
        return (
          <Box key={f.key} mb={1.5}>
            {fieldLabel}
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              multiline
              placeholder={f.label}
              value={data[f.key] || ''}
              onChange={(e) => setField(f.key, e.target.value)}
              error={missing}
              helperText={missing ? 'Required' : ''}
            />
          </Box>
        )
      })}
      <Typography variant="caption" color="textSecondary">
        All fields are required. Saved as a structured record; a readable
        summary is generated automatically.
      </Typography>
    </Box>
  )
}

export default InteractionTemplate
